/* eslint-disable unused-imports/no-unused-vars */
import { Dispatch, FormEvent, SetStateAction, useCallback, useEffect, useState } from "react";

import { InteractionObjectType, InteractionType, ProjectIssuanceTransactionConstants } from "../../../../constants";
import { PillData, ProjectIssuanceTransactionPanelData } from "../../../../models";
import { addOrRemoveObjectInteractions } from "../../../../service/interaction";
import { createIssuanceTransaction } from "../../../../service/project";
import {
  GetProjectIssuancesResponse,
  searchInteractions,
  SearchIssuanceTransactionsResponse,
} from "../../../../service/query";
import { ServiceError, Status } from "../../../../service/Shared";
import { useAuth } from "../../../../useAuth";
import { useIsLoadingWrapper } from "../../../../utils";
import { dataGridMapFilterCriteria, Toast } from "../../../../widget";
import { InteractionTag } from "../../../TagsPanel/models";

interface UseAddOrLinkIssuanceTransactionPanelProps {
  show: boolean;
  issuance?: GetProjectIssuancesResponse["results"][0];
  issuanceTransaction?: SearchIssuanceTransactionsResponse["results"][0];
  onClose: () => void;
  handleRefresh: () => void;
}

interface UseAddOrLinkIssuanceTransactionPanelReturnData {
  issuanceTransactionPanelData: ProjectIssuanceTransactionPanelData;
  setIssuanceTransactionPanelData: Dispatch<SetStateAction<ProjectIssuanceTransactionPanelData>>;
  errors: ServiceError[] | undefined;
  isHandleSubmitLoading: boolean;
  handleClosePanel: () => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  showReleaseToInventoryModal: boolean;
  releaseToInventoryType: string | undefined;

  showTagsPanel: boolean;
  setShowTagsPanel: Dispatch<SetStateAction<boolean>>;
  changedTagsExternal: InteractionTag[];
  setChangedTagsExternal: Dispatch<SetStateAction<InteractionTag[]>>;
  setRefreshTags: Dispatch<SetStateAction<boolean>>;
  tagPills: PillData[];
}

export const useAddOrLinkIssuanceTransactionPanel = ({
  show,
  issuance,
  issuanceTransaction,
  onClose,
  handleRefresh,
}: UseAddOrLinkIssuanceTransactionPanelProps): UseAddOrLinkIssuanceTransactionPanelReturnData => {
  const { currentOrganisationUuid } = useAuth();

  const [errors, setErrors] = useState<ServiceError[] | undefined>();
  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);

  const [showReleaseToInventoryModal, setShowReleaseToInventoryModal] = useState(false);
  const [releaseToInventoryType, setReleaseToInventoryType] = useState<string>();

  const [showTagsPanel, setShowTagsPanel] = useState(false);
  const [initialTagsFetched, setInitialTagsFetched] = useState(false); // For setting default tags for transactions with parents

  const [tagPills, setTagPills] = useState<PillData[]>([]);
  const [changedTagsExternal, setChangedTagsExternal] = useState<InteractionTag[]>([]);
  const [refreshTags, setRefreshTags] = useState(false);

  // eslint-disable-next-line no-undef
  const [issuanceTransactionPanelData, setIssuanceTransactionPanelData] = useState<ProjectIssuanceTransactionPanelData>(
    {
      ...ProjectIssuanceTransactionConstants.DEFAULT_ISSUANCE_TRANSACTION,
      issuanceUuid: issuance?.uuid ?? issuanceTransaction?.issuanceUuid ?? null,
      parentTransactionUuid: issuanceTransaction?.uuid ?? null,
      broker: issuanceTransaction?.broker ?? null,
      counterParty: issuanceTransaction?.counterParty ?? null,
      quantity: issuanceTransaction?.quantity ?? null,
      price: issuanceTransaction?.price ?? null,
      fees: issuanceTransaction?.fees ?? null,
      totalValue: issuanceTransaction?.totalValue ?? null,
    }
  );

  const handleResetPanel = (): void => {
    setIssuanceTransactionPanelData({
      ...ProjectIssuanceTransactionConstants.DEFAULT_ISSUANCE_TRANSACTION,
      issuanceUuid: issuance?.uuid ?? issuanceTransaction?.issuanceUuid ?? null,
      parentTransactionUuid: issuanceTransaction?.uuid ?? null,
      broker: issuanceTransaction?.broker ?? null,
      counterParty: issuanceTransaction?.counterParty ?? null,
      quantity: issuanceTransaction?.quantity ?? null,
      price: issuanceTransaction?.price ?? null,
      fees: issuanceTransaction?.fees ?? null,
      totalValue: issuanceTransaction?.totalValue ?? null,
    });
    setErrors([]);
  };

  const handleClosePanel = (): void => {
    onClose();
    setIssuanceTransactionPanelData({
      ...ProjectIssuanceTransactionConstants.DEFAULT_ISSUANCE_TRANSACTION,
    });
    setShowReleaseToInventoryModal(false);
    setReleaseToInventoryType(undefined);
    setInitialTagsFetched(false);
    setTagPills([]);
    setChangedTagsExternal([]);
    setErrors([]);
  };
  const handleSubmit = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors([]);

    const res = await createIssuanceTransaction({
      issuanceTransactions: [
        {
          areaHa: issuanceTransactionPanelData.areaHa,
          broker: issuanceTransactionPanelData.broker,
          counterParty: issuanceTransactionPanelData.counterParty ?? "",
          externalReference: issuanceTransactionPanelData.externalReference,
          fees: issuanceTransactionPanelData.fees,
          issuanceUuid: issuanceTransactionPanelData.issuanceUuid ?? "",
          parentTransactionUuid: issuanceTransactionPanelData.parentTransactionUuid,
          price: issuanceTransactionPanelData.price,
          quantity: issuanceTransactionPanelData.quantity ?? 0,
          totalValue:
            issuanceTransactionPanelData.totalValue !== null
              ? parseFloat(issuanceTransactionPanelData.totalValue.toFixed(5))
              : null,
          transactionDate: issuanceTransactionPanelData.transactionDate,
          type: issuanceTransactionPanelData.type ?? "",
        },
      ],
    });

    if (res.status === Status.Success) {
      const createdTransactionUuid = res.data?.issuanceTransactions?.[0]?.uuid;
      if (changedTagsExternal && changedTagsExternal.length > 0 && createdTransactionUuid) {
        await addOrRemoveObjectInteractions({
          objectInteractions: changedTagsExternal
            .filter((tag) => tag.checked)
            .map((tag) => ({
              interactionUuid: tag.uuid,
              objectType: InteractionObjectType.ISSUANCE_TRANSACTION,
              objectUuid: createdTransactionUuid,
              operation: "add",
            })),
        });
      }
      Toast.success({ message: "Issuance transaction created successfully" });

      if ((issuanceTransaction?.quantity ?? 0) > (issuanceTransactionPanelData.quantity ?? 0)) {
        setShowReleaseToInventoryModal(true);
        setReleaseToInventoryType(issuanceTransaction?.type);
      } else {
        handleRefresh();
        handleClosePanel();
      }
    }

    if (res.status === Status.Error) {
      setErrors(res.errors);
    }
  }, setIsHandleSubmitLoading);

  const fetchTags = useCallback(
    async (uuid: string, orgUuid: string): Promise<void> => {
      const filterCriteria = dataGridMapFilterCriteria([]);
      filterCriteria.type = {
        operator: "eq",
        value: InteractionType.TAG,
      };
      filterCriteria.objectType = {
        operator: "eq",
        value: InteractionObjectType.ORGANISATION,
      };
      filterCriteria.objectUuid = {
        operator: "eq",
        value: orgUuid,
      };
      filterCriteria.objectInteractions = {
        some: {
          objectUuid: {
            operator: "eq",
            value: uuid,
          },
          endDate: {
            operator: "eq",
            value: null,
          },
        },
      };
      const response = await searchInteractions({
        paging: {
          beforeCursor: null,
          afterCursor: null,
          limit: 50,
        },
        filter: { results: filterCriteria },
      });

      if (response.data?.results != null) {
        const existingTags =
          response.data.results.map(
            (x) =>
              ({
                uuid: x.uuid,
                data: JSON.parse(x.data),
                checked: true,
                rowVersion: x.rowVersion,
              }) as InteractionTag
          ) ?? [];
        setInitialTagsFetched(() => true);
        setTagPills(() => existingTags.map((tag) => ({ key: tag.uuid, label: tag.data.name })));
        setChangedTagsExternal(() => existingTags);
      }
    },
    [initialTagsFetched, issuanceTransaction?.uuid, currentOrganisationUuid, refreshTags]
  );

  useEffect(() => {
    setIssuanceTransactionPanelData({
      ...ProjectIssuanceTransactionConstants.DEFAULT_ISSUANCE_TRANSACTION,
      issuanceUuid: issuance?.uuid ?? issuanceTransaction?.issuanceUuid ?? null,
      parentTransactionUuid: issuanceTransaction?.uuid ?? null,
      broker: issuanceTransaction?.broker ?? null,
      counterParty: issuanceTransaction?.counterParty ?? null,
      quantity: issuanceTransaction?.quantity ?? null,
      price: issuanceTransaction?.price ?? null,
      fees: issuanceTransaction?.fees ?? null,
      totalValue: issuanceTransaction?.totalValue ?? null,
    });
  }, [issuance?.uuid, issuanceTransaction?.uuid, show]);

  useEffect(() => {
    if (show && !initialTagsFetched && issuanceTransaction?.uuid && currentOrganisationUuid) {
      fetchTags(issuanceTransaction.uuid, currentOrganisationUuid);
    } else if (show) {
      setTagPills(changedTagsExternal.filter((x) => x.checked).map((x) => ({ key: x.uuid, label: x.data.name })));
    }
  }, [show, issuanceTransaction?.uuid, currentOrganisationUuid, refreshTags, changedTagsExternal]);

  return {
    errors,
    isHandleSubmitLoading,
    issuanceTransactionPanelData,
    setIssuanceTransactionPanelData,
    handleClosePanel,
    handleSubmit,
    showReleaseToInventoryModal,
    releaseToInventoryType,
    showTagsPanel,
    setShowTagsPanel,
    setChangedTagsExternal,
    changedTagsExternal,
    setRefreshTags,
    tagPills,
  };
};
